import { createSelector } from 'reselect'

import { selectLabelsByMailboxId } from 'selectors/labels/base'
import { selectLabelSearchResultsByTerm } from 'selectors/labelings/search'
import {
  selectTicketSearchOperatorFullValueMap,
  selectCurrentCommittedTicketSearchQueryCurrentPart,
} from 'selectors/search/base'

import { getAgentUsername } from 'util/agents'
import { emptyArr, isEmpty } from 'util/arrays'
import { filter } from 'util/objects'
import wrapSearchValueInQuotesIfNeeded from 'util/search/wrapSearchValueInQuotesIfNeeded'
import { escapeRegExp } from 'util/strings'

const selectModifierSuggestions = createSelector(
  selectCurrentCommittedTicketSearchQueryCurrentPart,
  currentPart => {
    const { operator, part } = currentPart || {}
    const suggestions = [
      { suggestion: 'my:', hint: 'state or drafts', searchQuery: 'my:' },
      {
        suggestion: 'is:',
        hint: 'state, unassigned or assigned',
        searchQuery: 'is:',
      },
      {
        suggestion: 'assignee:',
        hint: 'username, me or unassigned',
        searchQuery: 'assignee:',
      },
      {
        suggestion: 'mentions:',
        hint: 'username or me',
        searchQuery: 'mentions:',
      },
      {
        suggestion: 'rating:',
        hint: 'awesome, ok, bad',
        searchQuery: 'rating:',
      },
      {
        suggestion: 'group:',
        hint: `${app.t('group')} name or unassigned`,
        searchQuery: 'group:',
      },
      {
        suggestion: 'drafts:',
        hint: 'username or me',
        searchQuery: 'drafts:',
      },
      {
        suggestion: 'folder:',
        hint: 'folder name',
        searchQuery: 'folder:',
      },
      { suggestion: 'tag:', hint: 'tag name', searchQuery: 'tag:' },
    ]
    return sameArrayIfEmpty(
      suggestions.filter(value => {
        if (!currentPart.part) return true
        const { suggestion } = value
        if (suggestion === part || suggestion === `${operator}:`) return false
        return suggestion.match(new RegExp(`(^|:)${escapeRegExp(part)}`, 'i'))
      })
    )
  }
)

const selectMySuggestions = createSelector(
  selectCurrentCommittedTicketSearchQueryCurrentPart,
  currentPart => {
    if (!currentPart) return emptyArr
    const { operator, part, value } = currentPart
    const operatorMatch = operator === 'keywords' || operator === 'my'
    const suggestions = [
      { suggestion: 'unread', searchQuery: 'my:unread' },
      { suggestion: 'open', searchQuery: 'my:open' },
      { suggestion: 'drafts', searchQuery: 'my:drafts' },
      { suggestion: 'snoozed', searchQuery: 'my:snoozed' },
      { suggestion: 'closed', searchQuery: 'my:closed' },
      { suggestion: 'starred', searchQuery: 'my:starred' },
      { suggestion: 'deleted', searchQuery: 'my:deleted' },
    ]
    const isComplete = !!suggestions.find(x => x.searchQuery === part)
    if (isComplete) return suggestions
    return sameArrayIfEmpty(
      suggestions.filter(
        ({ searchQuery }) =>
          (operatorMatch &&
            searchQuery.match(new RegExp(`:${escapeRegExp(value)}`, 'i'))) ||
          part === 'my:'
      )
    )
  }
)

const selectIsSuggestions = createSelector(
  selectCurrentCommittedTicketSearchQueryCurrentPart,
  currentPart => {
    if (!currentPart) return emptyArr
    const { operator, part, value } = currentPart

    const operatorMatch = operator === 'keywords' || operator === 'is'
    const suggestions = [
      { suggestion: 'unread', searchQuery: 'is:unread' },
      { suggestion: 'open', searchQuery: 'is:open' },
      { suggestion: 'unassigned', searchQuery: 'is:unassigned' },
      { suggestion: 'assigned', searchQuery: 'is:assigned' },
      { suggestion: 'snoozed', searchQuery: 'is:snoozed' },
      { suggestion: 'closed', searchQuery: 'is:closed' },
      { suggestion: 'starred', searchQuery: 'is:starred' },
      { suggestion: 'deleted', searchQuery: 'is:deleted' },
      { suggestion: 'spam', searchQuery: 'is:spam' },
      { suggestion: 'rated', searchQuery: 'is:rated' },
    ]
    const isComplete = !!suggestions.find(x => x.searchQuery === part)
    if (isComplete) return suggestions
    return sameArrayIfEmpty(
      suggestions.filter(
        ({ searchQuery }) =>
          (operatorMatch &&
            searchQuery.match(new RegExp(`:${escapeRegExp(value)}`, 'i'))) ||
          part === 'is:'
      )
    )
  }
)

const makeSelectAgentSuggestions = inputOperator => {
  return createSelector(
    selectCurrentCommittedTicketSearchQueryCurrentPart,
    selectTicketSearchOperatorFullValueMap,
    buildAgentSuggestions(inputOperator)
  )
}

function buildAgentSuggestions(inputOperator) {
  return (currentPart, valueMap) => {
    if (!currentPart) return emptyArr

    const { operator, part, value } = currentPart || {}
    const { assignee: agents } = valueMap
    const { me } = agents

    if (!me) return emptyArr

    const results = Object.keys(agents)
      .map(key => {
        const agent = agents[key]
        if (key === 'me' || agent.username === me.username) return null
        const username = getAgentUsername(agent)
        return {
          avatar_url: agent.avatar_url,
          suggestion: username || agent.name,
          name: agent.name,
          hint: agent.name,
          username,
          searchQuery: `${inputOperator}:${wrapSearchValueInQuotesIfNeeded(
            key
          )}`,
        }
      })
      .filter(agent => !!(agent && agent.username))
      .sort((a, b) => {
        if (a.username < b.username) return -1
        if (a.username > b.username) return 1
        return 0
      })

    if (me) {
      const username = getAgentUsername(me)
      results.unshift({
        avatar_url: me.avatar_url,
        suggestion: username,
        name: me.name,
        hint: 'You',
        username,
        searchQuery: `${inputOperator}:me`,
        keywords: 'me you',
      })
    }

    if (inputOperator === 'assignee') {
      results.unshift({
        nullValue: true,
        suggestion: 'Unassigned',
        hint: `not assigned to ${app.t('agent_with_article')}`,
        searchQuery: `${inputOperator}:unassigned`,
      })
    }

    const operatorMatch = operator === 'keywords' || operator === inputOperator
    const isComplete = !!results.find(x => x.searchQuery === part)

    if (isComplete) return results

    return sameArrayIfEmpty(
      results.filter(result => {
        if (!result) return false
        const { suggestion, name, username, keywords } = result
        const matchAgainst = [null, suggestion, name, username, keywords].join(
          ' '
        )
        return (
          (operatorMatch &&
            matchAgainst.match(
              new RegExp(`(^|:|\\s)${escapeRegExp(value)}`, 'i')
            )) ||
          part === `${inputOperator}:`
        )
      })
    )
  }
}

const selectAssigneeSuggestions = makeSelectAgentSuggestions('assignee')
const selectMentionsSuggestions = makeSelectAgentSuggestions('mentions')
const selectDraftSuggestions = makeSelectAgentSuggestions('drafts')

const selectGroupSuggestions = createSelector(
  selectCurrentCommittedTicketSearchQueryCurrentPart,
  selectTicketSearchOperatorFullValueMap,
  (currentPart, valueMap) => {
    if (!currentPart) return emptyArr

    const { operator, part, value } = currentPart
    const { assigned_group: groups } = valueMap
    const results = Object.values(groups).map(group => ({
      suggestion: group.name,
      searchQuery: `group:${wrapSearchValueInQuotesIfNeeded(group.name)}`,
    }))
    results.unshift({
      nullValue: true,
      suggestion: 'Unassigned',
      hint: `not assigned to ${app.t('group')}`,
      searchQuery: `group:unassigned`,
    })

    const operatorMatch = operator === 'keywords' || operator === 'group'
    const isComplete = !!results.find(x => x.searchQuery === part)

    if (isComplete) return results

    return sameArrayIfEmpty(
      results.filter(result => {
        if (!result) return false
        return (
          (operatorMatch &&
            result.searchQuery.match(
              new RegExp(`(:|\\s)${escapeRegExp(value)}`, 'i')
            )) ||
          part === 'group:'
        )
      })
    )
  }
)

function mapLabel(label) {
  return {
    suggestion: label.name,
    searchQuery: `tag:${wrapSearchValueInQuotesIfNeeded(label.name)}`,
  }
}

const selectLabelSuggestions = createSelector(
  selectCurrentCommittedTicketSearchQueryCurrentPart,
  selectLabelSearchResultsByTerm,
  selectLabelsByMailboxId,
  (currentPart, labelsByTerm, labelsByMailbox) => {
    if (!currentPart) return null
    const { operator, part, value } = currentPart || {}
    const loading = [{ isLoading: true }]

    const topLabels = labelsByMailbox.null
    const results = labelsByTerm[value]
    if (part === 'tag:') {
      if (topLabels) return topLabels.map(mapLabel)
      return loading
    }

    const operatorMatch = operator === 'keywords' || operator === 'tag'
    if (results && operatorMatch) {
      const size = operator === 'tag' ? 20 : 5
      return sameArrayIfEmpty(results.slice(0, size).map(mapLabel))
    }
    if (operatorMatch) return loading
    return null
  }
)

const selectRatingSuggestions = createSelector(
  selectCurrentCommittedTicketSearchQueryCurrentPart,
  currentPart => {
    if (!currentPart) return emptyArr
    const { operator, part, value } = currentPart

    const operatorMatch = operator === 'rating'
    const suggestions = [
      { suggestion: 'awesome', searchQuery: 'rating:awesome' },
      { suggestion: 'ok', searchQuery: 'rating:ok' },
      { suggestion: 'bad', searchQuery: 'rating:bad' },
    ]
    const isComplete = !!suggestions.find(x => x.searchQuery === part)
    if (isComplete) return suggestions
    return sameArrayIfEmpty(
      suggestions.filter(
        ({ searchQuery }) =>
          (operatorMatch &&
            searchQuery.match(new RegExp(`:${escapeRegExp(value)}`, 'i'))) ||
          part === 'rating:'
      )
    )
  }
)

const selectFolderSuggestions = createSelector(
  selectCurrentCommittedTicketSearchQueryCurrentPart,
  selectTicketSearchOperatorFullValueMap,
  (currentPart, valueMap) => {
    if (!currentPart) return emptyArr

    const { operator, part, value } = currentPart || {}
    const { folder: folders } = valueMap
    const results = Object.values(folders).map(folder => ({
      suggestion: folder.name,
      name: folder.name,
      searchQuery: `folder:${wrapSearchValueInQuotesIfNeeded(folder.name)}`,
    }))
    const operatorMatch = operator === 'keywords' || operator === 'folder'
    const isComplete = !!results.find(x => x.searchQuery === part)

    if (isComplete) return results

    return sameArrayIfEmpty(
      results.filter(result => {
        if (!result) return false
        const { suggestion, name, hint } = result
        const matchAgainst = [null, suggestion, name, hint].join(' ')
        return (
          (operatorMatch &&
            matchAgainst.match(
              new RegExp(`(:|\\s)${escapeRegExp(value)}`, 'i')
            )) ||
          part === 'folder:'
        )
      })
    )
  }
)

export const selectTicketSearchSuggestions = createSelector(
  selectCurrentCommittedTicketSearchQueryCurrentPart,
  selectModifierSuggestions,
  selectMySuggestions,
  selectIsSuggestions,
  selectAssigneeSuggestions,
  selectGroupSuggestions,
  selectDraftSuggestions,
  selectLabelSuggestions,
  selectMentionsSuggestions,
  selectRatingSuggestions,
  selectFolderSuggestions,
  (
    currentPart,
    modifier,
    my,
    is,
    assignee,
    group,
    drafts,
    label,
    mentions,
    rating,
    folder
  ) => {
    return filter(value => !!value, {
      [currentPart.part ? 'modifiers' : 'narrow']: modifier,
      my,
      is,
      assignee,
      group,
      drafts,
      label,
      mentions,
      rating,
      folder,
    })
  }
)

export const selectIsTicketSearchSuggestions = createSelector(
  selectTicketSearchSuggestions,
  suggestions => {
    const allSuggestionsCount = Object.values(suggestions).reduce(
      (output, value) => {
        return output + value.length
      },
      0
    )
    return allSuggestionsCount !== 0
  }
)

// perf - dont return a brand new array object if you dont need to.
// Return the frozen one instead.
function sameArrayIfEmpty(array) {
  if (isEmpty(array)) return emptyArr
  return array
}
